import React from 'react';
import { Anchor, Box, Burger, Button, Container, Divider, Drawer, Group, Menu, ScrollArea, SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import logo from './../images/general/companyLogoDesktopMobile.webp';
import logo2x from './../images/general/companyLogoDesktopMobile-108.webp';
import type { Tour } from '../types';

interface SiteHeaderProps {
  tours: Tour[];
  arrivalMode: 'hotel' | 'cruise';
  onArrivalChange: (mode: 'hotel' | 'cruise') => void;
}

const primaryLinks = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/About' },
  { label: 'Contact', to: '/Contact' },
];

const tourLinks = [
  { label: 'All Tours', to: '/tours/allTours' },
  { label: 'Cruise Tours', to: '/tours/cruiseTours' },
];

function SiteHeader({ tours, arrivalMode, onArrivalChange }: SiteHeaderProps) {
  const [opened, { toggle, close }] = useDisclosure(false);

  return (
    <Box
      component="header"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid #e9ecef',
      }}
    >
      <Container size="lg" py="sm">
        <Group justify="space-between" align="center">
          <Group gap="sm" align="center">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label={opened ? "Close navigation menu" : "Open navigation menu"}
            />
            <Anchor component={Link} to="/" aria-label="Placencia Action Tours home" style={{ display: 'inline-flex', alignItems: 'center' }}>
              <img
                src={logo}
                srcSet={`${logo} 1x, ${logo2x} 2x`}
                alt="Placencia Action Tours logo"
                style={{ height: 54 }}
                width={54}
                height={54}
              />
            </Anchor>
            <Text fw={700} size="xl" visibleFrom="sm">Placencia Action Tours</Text>
          </Group>

          <Group gap="xs" visibleFrom="sm">
            <SegmentedControl
              value={arrivalMode}
              onChange={(value) => {
                if (value === 'hotel' || value === 'cruise') onArrivalChange(value);
              }}
              data={[
                {
                  label: (
                    <Tooltip
                      label={arrivalMode === 'hotel'
                        ? "You plan on embarking from a hotel in Placencia"
                        : "Click to show available tours for hotel guests"}
                      withArrow
                    >
                      <span>Hotel</span>
                    </Tooltip>
                  ),
                  value: 'hotel',
                },
                {
                  label: (
                    <Tooltip
                      label={arrivalMode === 'cruise'
                        ? "You plan on embarking from the cruise docks"
                        : "Click to show available tours from the Cruise docks"}
                      withArrow
                    >
                      <span>Cruise</span>
                    </Tooltip>
                  ),
                  value: 'cruise',
                },
              ]}
              size="xs"
              radius="xl"
              color="brand"
            />
            {primaryLinks.map((link) => (
              <Button
                key={link.to}
                component={Link}
                to={link.to}
                variant="subtle"
                color="brand"
              >
                {link.label}
              </Button>
            ))}
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <Button variant="subtle" color="brand">Tours</Button>
              </Menu.Target>
              <Menu.Dropdown>
                {tourLinks.map((link) => (
                  <Menu.Item key={link.to} component={Link} to={link.to}>
                    {link.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
            <Button component={Link} to="/BookNow" color="brand" radius="xl">
              Book now
            </Button>
          </Group>
        </Group>
      </Container>

      <Drawer
        opened={opened}
        onClose={close}
        size="sm"
        title="Explore"
        padding="md"
        hiddenFrom="sm"
        withCloseButton
      >
        <ScrollArea h="70vh">
          <Stack gap="sm">
            {primaryLinks.map((link) => (
              <Button
                key={`mobile-${link.to}`}
                component={Link}
                to={link.to}
                variant="subtle"
                color="brand"
                onClick={close}
                fullWidth
              >
                {link.label}
              </Button>
            ))}
            <Divider label="Arriving by" />
            <SegmentedControl
              value={arrivalMode}
              onChange={(value) => {
                if (value === 'hotel' || value === 'cruise') onArrivalChange(value);
              }}
              data={[
                {
                  label: (
                    <Tooltip
                      label={arrivalMode === 'hotel'
                        ? "You plan on embarking from a hotel in Placencia"
                        : "Click to show available tours for hotel guests"}
                      withArrow
                    >
                      <span>Hotel</span>
                    </Tooltip>
                  ),
                  value: 'hotel',
                },
                {
                  label: (
                    <Tooltip
                      label={arrivalMode === 'cruise'
                        ? "You plan on embarking from the cruise docks"
                        : "Click to show available tours from the Cruise docks"}
                      withArrow
                    >
                      <span>Cruise</span>
                    </Tooltip>
                  ),
                  value: 'cruise',
                },
              ]}
              size="sm"
              radius="xl"
              color="brand"
              fullWidth
            />
            <Divider label="Tours" />
            {tourLinks.map((link) => (
              <Button
                key={`mobile-${link.to}`}
                component={Link}
                to={link.to}
                variant="light"
                color="brand"
                onClick={close}
                fullWidth
              >
                {link.label}
              </Button>
            ))}
            <Divider label="All tours" />
            <Stack gap={6}>
              {tours.map((tour) => (
                <Anchor
                  key={tour.name}
                  component={Link}
                  to={tour.url}
                  onClick={close}
                  c="brand.7"
                  fw={500}
                >
                  {tour.text}
                </Anchor>
              ))}
            </Stack>
            <Divider />
            <Button component={Link} to="/BookNow" color="brand" radius="xl" onClick={close}>
              Book now
            </Button>
          </Stack>
        </ScrollArea>
      </Drawer>
    </Box>
  );
}

export default SiteHeader;
